<script setup lang="ts">
import {
  getCalList,
  getWarehouseList,
  queryCargoType,
  queryCountryList,
} from '@user-center/api/common.ts'
import { $t } from '@user-center/utils/i18n'
import { storeData } from '@user-center/utils/localService.ts'
import { ElMessage } from 'element-plus'
import { onMounted, reactive, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { normalizeEstimateWeight } from '@/utils/mallShippingEstimate'
import LogisticsCard from './LogisticsCard.vue'

const router = useRouter()
const route = useRoute()

const queryData = reactive({
  warehouseCode: '',
  countryCode: '',
  cargoTypeCode: '',
  weight: '',

})
const state = reactive({
  transfersMap: {}

})


const warehouseList = ref<any[]>([])
function getStoreInformation() {
  if (!process.client) {
    return null
  }

  const storeInformation = localStorage.getItem(storeData.StoreInformation)
  if (!storeInformation) {
    return null
  }

  try {
    return JSON.parse(storeInformation)
  }
  catch {
    return null
  }
}

function readRouteQueryValue(key: string) {
  const rawValue = route.query[key]
  if (Array.isArray(rawValue)) {
    return String(rawValue[0] || '').trim()
  }

  return String(rawValue || '').trim()
}

function shouldAutoQueryFromRoute() {
  const rawAutoQuery = readRouteQueryValue('autoQuery').toLowerCase()
  if (!rawAutoQuery) {
    return Boolean(readRouteQueryValue('countryCode') && readRouteQueryValue('weight'))
  }

  return ['1', 'true', 'yes'].includes(rawAutoQuery)
}

function applyRoutePrefill() {
  queryData.warehouseCode = readRouteQueryValue('warehouseCode')
  queryData.countryCode = readRouteQueryValue('countryCode')
  queryData.cargoTypeCode = readRouteQueryValue('cargoTypeCode')
  queryData.weight = normalizeEstimateWeight(readRouteQueryValue('weight'))
}

async function queryWarehouseList() {
  const storeInformation = getStoreInformation()
  if (!storeInformation?.id) {
    warehouseList.value = []
    return []
  }

  const res: any = await getWarehouseList({ shopId: storeInformation.id })
  if (res.transfersMap) {
    state.transfersMap = {
      ...state.transfersMap,
      ...res.transfersMap
    }
  }

  const list = Array.isArray(res.data) ? res.data : []
  warehouseList.value = list
  return list
}

const countryList = ref<any[]>([])
const countryCodeMap = ref({})
async function getCountryListByWarehouse(
  warehouseCode = queryData.warehouseCode,
  options: { syncState?: boolean } = {},
) {
  const storeInformation = getStoreInformation()
  const normalizedWarehouseCode = String(warehouseCode || '').trim()

  if (!storeInformation?.id || !normalizedWarehouseCode) {
    if (options.syncState !== false) {
      countryList.value = []
      countryCodeMap.value = {}
    }
    return []
  }

  const res: any = await queryCountryList({
    shopId: storeInformation.id,
    warehouseCode: normalizedWarehouseCode,
  })

  if (res.transfersMap) {
    state.transfersMap = {
      ...state.transfersMap,
      ...res.transfersMap
    }
  }

  const list = Array.isArray(res.data) ? res.data : []
  const dataMap = {}
  list.forEach((country: any) => {
    dataMap[country.code] = country
  })

  if (options.syncState !== false) {
    countryList.value = list
    countryCodeMap.value = dataMap
  }

  return list
}

const categoryList = ref<any[]>([])
async function getCategoryList() {
  const storeInformation = getStoreInformation()
  if (!storeInformation?.id || !queryData.warehouseCode || !queryData.countryCode) {
    categoryList.value = []
    return []
  }

  const res: any = await queryCargoType({
    shopId: storeInformation.id,
    dealerShopId: storeInformation.id,
    warehouseCode: queryData.warehouseCode,
    attrTypeValue: countryCodeMap.value[queryData.countryCode]?.country,
  })

  if (res.transfersMap) {
    state.transfersMap = {
      ...state.transfersMap,
      ...res.transfersMap
    }
  }

  const list = Array.isArray(res.data) ? res.data : []
  categoryList.value = list
  return list
}

async function handleWarehouseChange(nextWarehouseCode = queryData.warehouseCode, options: {
  preferredCountryCode?: string
  preferredCargoTypeCode?: string
} = {}) {
  queryData.warehouseCode = String(nextWarehouseCode || '').trim()
  queryData.countryCode = ''
  queryData.cargoTypeCode = ''

  if (!queryData.warehouseCode) {
    countryList.value = []
    countryCodeMap.value = {}
    categoryList.value = []
    return
  }

  const list = await getCountryListByWarehouse()
  const preferredCountryCode = String(options.preferredCountryCode || '').trim()
  const nextCountryCode = preferredCountryCode && list.some((item: any) => String(item?.code || '').trim() === preferredCountryCode)
    ? preferredCountryCode
    : String(list[0]?.code || '').trim()

  if (!nextCountryCode) {
    return
  }

  await handleCountryChange(nextCountryCode, {
    preferredCargoTypeCode: options.preferredCargoTypeCode,
  })
}

async function handleCountryChange(nextCountryCode = queryData.countryCode, options: {
  preferredCargoTypeCode?: string
} = {}) {
  queryData.countryCode = String(nextCountryCode || '').trim()
  queryData.cargoTypeCode = ''

  if (!queryData.countryCode) {
    categoryList.value = []
    return
  }

  const list = await getCategoryList()
  const preferredCargoTypeCode = String(options.preferredCargoTypeCode || '').trim()

  if (preferredCargoTypeCode && list.some((item: any) => String(item?.attrValue || '').trim() === preferredCargoTypeCode)) {
    queryData.cargoTypeCode = preferredCargoTypeCode
  }
}

const list = ref([])
async function handleQuery(options: { silentValidation?: boolean } = {}) {
  if (!queryData.warehouseCode || !queryData.countryCode  || !queryData.weight) {
    if (!options.silentValidation) {
      ElMessage.error({
        message: $t('estimation.fillRequiredInfo'),
        plain: true,
        grouping: true,
      })
    }
    return
  }
  const res: any = await getCalList({
    ...queryData,
  })
  if (res.transfersMap) {
          state.transfersMap = {
            ...state.transfersMap,
            ...res.transfersMap
          }
        }
  const rawList = Array.isArray(res.data) ? res.data : []
  const uniqueList = rawList.filter((item: any, index: number, self: any[]) =>
    index === self.findIndex(t => t.id === item.id),
  )
  list.value = uniqueList
}

const showLogistic = ref('')
function toggleLogistic(id: string) {
  showLogistic.value = showLogistic.value === id ? '' : id
}

const showBackBtn = ref(false)

function getBackPath() {
  const from = Array.isArray(route.query.from)
    ? route.query.from[0]
    : route.query.from

  if (from === 'cart') {
    return '/mall/cart'
  }

  return '/mall/home'
}

async function initializePage() {
  applyRoutePrefill()

  const warehouseItems = await queryWarehouseList()
  if (!warehouseItems.length) {
    return
  }

  const hasRoutePrefill = Boolean(
    queryData.warehouseCode
    || queryData.countryCode
    || queryData.cargoTypeCode
    || queryData.weight
    || readRouteQueryValue('autoQuery'),
  )

  if (!hasRoutePrefill) {
    return
  }

  const preferredWarehouseCode = String(queryData.warehouseCode || '').trim()
  const preferredCountryCode = readRouteQueryValue('countryCode')
  let nextWarehouseCode = preferredWarehouseCode
    && warehouseItems.some((item: any) => String(item?.warehouseCode || '').trim() === preferredWarehouseCode)
    ? preferredWarehouseCode
    : ''

  if (!nextWarehouseCode && preferredCountryCode) {
    for (const warehouseItem of warehouseItems) {
      const candidateWarehouseCode = String(warehouseItem?.warehouseCode || '').trim()
      if (!candidateWarehouseCode) {
        continue
      }

      const supportedCountries = await getCountryListByWarehouse(candidateWarehouseCode, {
        syncState: false,
      })
      const matchedCountry = supportedCountries.some((item: any) => String(item?.code || '').trim() === preferredCountryCode)
      if (matchedCountry) {
        nextWarehouseCode = candidateWarehouseCode
        break
      }
    }
  }

  if (!nextWarehouseCode) {
    nextWarehouseCode = String(warehouseItems[0]?.warehouseCode || '').trim()
  }

  if (!nextWarehouseCode) {
    return
  }

  await handleWarehouseChange(nextWarehouseCode, {
    preferredCountryCode,
    preferredCargoTypeCode: readRouteQueryValue('cargoTypeCode'),
  })

  if (shouldAutoQueryFromRoute()) {
    await handleQuery({ silentValidation: true })
  }
}

function syncRouteState() {
  showBackBtn.value = Boolean(route.query.from)
}

onMounted(() => {
  syncRouteState()
  initializePage()
})

watch(
  () => route.fullPath,
  () => {
    syncRouteState()
    initializePage()
  },
)

function goBack() {
  router.replace(getBackPath())
}
</script>

<template>
  <div class="estimation-wrapper">
    <div class="header-title-wrapper">
      <h2 class="header-title">
        {{ $t('estimation.topTitle') }}
      </h2>
    </div>
    <div class="header-search-wrapper max-w-1200px mx-auto">
      <div class="search-header">
        <el-button @click="goBack">
          <!-- <span class="back-icon">&lt;</span> -->
          {{ $t("actions.back") }}
        </el-button>
        <h4 class="search-title">
          {{ $t('estimation.title') }}
        </h4>
        
      </div>
      <div class="content-wrapper">
        <div class="search-info">
          <div class="search-item">
            <el-select v-model="queryData.warehouseCode" class="base-ui-select"
              :placeholder="$t('placeholder.pleaseSelect', { text: $t('text.仓库') })" filterable
              @change="handleWarehouseChange">
              <el-option v-for="item in warehouseList" :key="item.warehouseCode" :value="item.warehouseCode"
                :label="state.transfersMap[item.warehouseName]?state.transfersMap[item.warehouseName]:item.warehouseName" />
              <template #prefix>
                <span class="prefix-label">
                  <b class="required-icon">*</b>
                  {{ $t('text.仓库') }}
                </span>
              </template>
            </el-select>
          </div>

          <div class="search-item">
            <el-select v-model="queryData.countryCode" class="base-ui-select"
              :placeholder="$t('placeholder.pleaseSelect', { text: $t('text.destination') })" filterable
              :no-data-text="queryData.countryCode ? $t('text.noData') : $t('placeholder.pleaseSelect', { text: $t('text.仓库') })"
              @change="handleCountryChange">
              <el-option v-for="item in countryList" :key="item.code" :value="item.code"
                :label="state.transfersMap[item.aliasName||item.country]?state.transfersMap[item.aliasName||item.country]:(item.aliasName||item.country)" />
              <template #prefix>
                <span class="prefix-label">
                  <b class="required-icon">*</b>
                  {{ $t('estimation.formWarehouseTo') }}
                </span>
              </template>
            </el-select>
          </div>

          <div class="search-item">
            <el-select v-model="queryData.cargoTypeCode" class="base-ui-select"
              :placeholder="$t('placeholder.pleaseInput', { text: $t('text.货物类型') })"
              :no-data-text="queryData.countryCode ? $t('text.noData') : $t('placeholder.pleaseSelect', { text: $t('text.destination') })">
              <el-option v-for="item in categoryList" :key="item.attrValue" :value="item.attrValue"
                :label="state.transfersMap[item.userDef1]?state.transfersMap[item.userDef1]:item.userDef1 " />
              <template #prefix>
                <span class="prefix-label">
              
                  {{ $t('text.货物类型') }}
                </span>
              </template>
            </el-select>
          </div>

          <div class="search-item">
            <el-input v-model="queryData.weight" class="base-ui-input" type="number"
              :placeholder="$t('placeholder.pleaseInput', { text: $t('text.重量') })">
              <template #prefix>
                <div class="prefix-label">
                  <b class="required-icon">*</b>
                  {{ $t('text.重量') }}(kg)
                </div>
              </template>
            </el-input>
          </div>
        </div>

        <el-button class="base-ui-primary inquire-btn" @click="handleQuery">
          {{ $t('actions.inquire') }}
        </el-button>

        <div class="w-full h-32px" />
        <!-- <p class="tip-style search-tip max-w-1440px mx-auto">
          {{ $t('estimation.tip') }}
        </p> -->
        <div class="min-h-80vh max-w-1200px mx-auto">
          <LogisticsCard v-for="item in list" :key="item.id" :item="item" :is-expanded="item.id === showLogistic"
            @toggle-details="toggleLogistic(item.id)" :transfersMap="state.transfersMap" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.estimation-wrapper .header-title-wrapper {
  align-items: center;
  background: url(https://hoobuy.com/_nuxt/estimation_bg.T50Etovp.webp) no-repeat 50%;
  background-size: cover;
  display: flex;
  height: 180px;
}

.estimation-wrapper .header-title {
  color: #fff;
  font-size: 32px;
  font-weight: 900;
  line-height: 36px;
  margin-left: calc((100vw - 1440px)/2 + 170px);
  width: 588px
}

.estimation-wrapper .header-search-wrapper {
  padding: 24px;
}

.estimation-wrapper .header-search-wrapper .search-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.estimation-wrapper .header-search-wrapper .back-btn {
  background: var(--el-color-primary);
  border: none;
  color: #fff;
  font-size: 14px;
  padding: 8px 16px;
}

.estimation-wrapper .header-search-wrapper .back-btn:hover {
  background: var(--el-color-primary-light-3);
}

.estimation-wrapper .header-search-wrapper .back-icon {
  margin-right: 4px;
}

.estimation-wrapper .search-title {
  flex: 1;
  color: var(--text);
  font-size: 24px;
  font-weight: 700;
  line-height: 30px;
  text-align: center;
  padding-right: 50px;
}

.estimation-wrapper .content-wrapper {
  margin-top: 24px
}

.estimation-wrapper .search-tip {
  color: var(--text-02);
  font-weight: 400;
  margin-top: 30px;
  padding: 10px 24px
}

.estimation-wrapper .search-info {
  display: grid;
  gap: 16px 24px;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 30px;
}

.estimation-wrapper .search-item {
  height: 50px;
}

.estimation-wrapper .search-item .base-ui-input {
  --el-input-height: 33px;
  --el-input-border-radius: 4px;
  --el-input-bg-color: #fff;
}

.estimation-wrapper .search-item .base-ui-select {
  --el-border-radius-base: 4px;
  --el-fill-color-blank: #fff;

  :deep(.el-select__wrapper) {
    padding: 8px 12px;
    height: 33px;
  }

  :deep(.el-select__selected-item) {
    text-align: right;
    // font-weight: 700;
    font-size: 14px;
    color: var(--text-01)
  }

  :deep(.el-select__input) {
    text-align: right;
  }
}

.estimation-wrapper .search-item .base-ui-input :deep(.el-input__inner) {
  text-align: right
}

.estimation-wrapper .prefix-label {
  align-items: center;
  color: var(--text);
  display: flex;
  font-size: 12px;
  font-weight: 700;
}

.estimation-wrapper .prefix-label .required-icon {
  color: var(--el-color-danger);
  font-size: 16px;
  height: 10px;
  line-height: 20px;
  margin-right: 4px;
  width: 8px;
}

.estimation-wrapper .inquire-btn {
  display: block;
  font-size: 18px;
  font-weight: 900;
  height: 60px;
  margin: 30px auto 0;
  min-width: 200px;
  --el-border-radius-base: 60px;
}
</style>
